import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageToggle from '../components/LanguageToggle';
import axios from 'axios';
import { Volleyball, EnvelopeSimple, ArrowLeft } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatApiErrorDetail(detail, fallback) {
  if (detail == null) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(e => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e))).join(' ');
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
}

export default function ForgotPassword() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setStatus('loading');
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setStatus('sent');
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail, t('forgot.sendError')));
      setStatus('error');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FDFBF7] px-4">
      <div className="fixed top-4 right-4 z-50"><LanguageToggle /></div>
      <div className="w-full max-w-md">
        <Link
          to="/login"
          className="inline-flex items-center gap-2 text-[#73706A] hover:text-[#1A1A1A] mb-6 text-sm"
          data-testid="back-to-login"
        >
          <ArrowLeft size={16} /> {t('forgot.backToLogin')}
        </Link>

        <div className="bg-white border border-[#E6E2D8] rounded-xl p-8 shadow-sm">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 bg-[#E85D04] rounded-xl flex items-center justify-center">
              <Volleyball size={28} className="text-white" weight="fill" />
            </div>
            <div>
              <h1 className="font-['Outfit'] text-2xl font-bold text-[#1A1A1A]">{t('forgot.title')}</h1>
              <p className="text-sm text-[#73706A]">Tia Karol Sports</p>
            </div>
          </div>

          {status === 'sent' ? (
            <div className="bg-green-50 border border-green-200 rounded-lg p-5" data-testid="forgot-password-sent">
              <div className="flex items-center gap-3 mb-2">
                <EnvelopeSimple size={24} className="text-[#2D6A4F]" weight="fill" />
                <h2 className="font-semibold text-[#2D6A4F]">{t('forgot.sentTitle')}</h2>
              </div>
              <p className="text-sm text-[#1A1A1A] leading-relaxed">
                {t('forgot.sentBody1')} <strong>{email}</strong> {t('forgot.sentBody2')}
              </p>
              <p className="text-xs text-[#73706A] mt-3">
                {t('forgot.spamNote')}
              </p>
              <Link
                to="/login"
                className="mt-4 inline-block text-sm text-[#E85D04] hover:text-[#DC2F02] font-medium"
                data-testid="forgot-password-back-btn"
              >
                {t('forgot.backToLogin')}
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} data-testid="forgot-password-form">
              <p className="text-sm text-[#1A1A1A] mb-5 leading-relaxed">
                {t('forgot.subtitle')}
              </p>

              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-[#9B2226] text-sm" data-testid="forgot-password-error">
                  {error}
                </div>
              )}

              <label className="block">
                <span className="text-sm font-medium text-[#1A1A1A]">{t('common.email')}</span>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input-field mt-1"
                  placeholder={t('login.emailPlaceholder')}
                  required
                  data-testid="forgot-password-email-input"
                />
              </label>

              <button
                type="submit"
                disabled={status === 'loading' || !email}
                className="mt-6 w-full btn-primary py-3 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="forgot-password-submit-btn"
              >
                {status === 'loading' ? t('forgot.sending') : t('forgot.sendButton')}
              </button>
            </form>
          )}
        </div>

        <p className="mt-6 text-center text-sm text-[#73706A]">
          {t('login.noAccount')}{' '}
          <Link to="/register" className="text-[#E85D04] hover:text-[#DC2F02] font-medium">
            {t('login.createAccount')}
          </Link>
        </p>
      </div>
    </div>
  );
}
